import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { GoogleMap, LoadScript, Libraries } from "@react-google-maps/api";
import { DEFAULT_ZOOM, DEFAULT_ZOOM_MOBILE } from "../../constants/map";
import { GeoLocation } from "../../types/common";
import useIsMobile from "../../hooks/useIsMobile";
import Marker from "./map/Marker";
import { BasicToolbar } from "./map/BasicToolbar";
import { SearchToolbar } from "./map/SearchToolbar";
import { MapPinOff } from "lucide-react";

export enum MapTypeId {
  Roadmap = 'roadmap',
  Satellite = 'satellite',
}

const LIBRARIES = ["places", "geometry"] as Libraries;

export interface MapRef {
  panTo: (location: GeoLocation) => void;
  setZoom: (zoom: number) => void;
  getMap: () => google.maps.Map | null;
}

interface MapProps {
  mapCenter: { lat: number; lng: number };
  zoom?: number;
  children?: React.ReactNode;
  onZoomChange?: (zoom: number) => void;
  isSuppressLandmarks?: boolean;
  initialMapTypeId?: MapTypeId;
  mapTypeId?: MapTypeId;
  onMapTypeChange?: (mapTypeId: MapTypeId) => void;
  onLocationRequest?: (location: GeoLocation) => void;
  isSuppressToolbar?: boolean;
  onMapLoad?: (map: google.maps.Map) => void;
  onAddressSelected?: (data: {address: any, location: GeoLocation, formattedAddress: string, smartyStreetAddress: any}) => void;
  suppressAddressMarker?: boolean;
  drawingMode?: string | null;
  selectedShapeType?: string | null;
  onDrawingModeChange?: (mode: string | null) => void;
  triggerSearchToolbarPulse?: boolean;
  enableSelectionToolbar?: boolean;
  /** Custom message to display when map center is invalid (0,0 or undefined) */
  noLocationMessage?: React.ReactNode;
}

export const Map = forwardRef<MapRef, MapProps>(({
  mapCenter,
  zoom: controlledZoom,
  children = null,
  onZoomChange,
  isSuppressLandmarks = false,
  initialMapTypeId = MapTypeId.Roadmap,
  mapTypeId: controlledMapTypeId,
  onMapTypeChange,
  onLocationRequest: _onLocationRequest,
  isSuppressToolbar = false,
  onMapLoad,
  onAddressSelected,
  suppressAddressMarker = false,
  drawingMode,
  selectedShapeType,
  onDrawingModeChange,
  triggerSearchToolbarPulse = false,
  enableSelectionToolbar = false,
  noLocationMessage = "No location data available",
}, ref) => {
  const isMobile = useIsMobile();
  const defaultZoom = isMobile ? DEFAULT_ZOOM_MOBILE : DEFAULT_ZOOM;

  const mapRef = useRef<google.maps.Map | null>(null);
  const [internalMapTypeId, setInternalMapTypeId] = useState<MapTypeId>(initialMapTypeId);
  const [internalZoom, setInternalZoom] = useState<number>(defaultZoom);
  const [selectedAddressLocation, setSelectedAddressLocation] = useState<GeoLocation | null>(null);
  const [addressMarkerIconUrl, setAddressMarkerIconUrl] = useState<string>("");
  const previousCenterRef = useRef<{ lat: number; lng: number } | null>(null);

  // Use controlled values if provided, otherwise use internal state
  const mapTypeId = controlledMapTypeId ?? internalMapTypeId;
  const zoom = controlledZoom ?? internalZoom;

  // Load address marker icon
  useEffect(() => {
    const loadAddressMarkerIcon = async () => {
      try {
        const icon = await import("../icons/products/map-markers/ns-orange.svg");
        setAddressMarkerIconUrl(icon.default);
      } catch (error) {
        console.error("Failed to load address marker icon:", error);
      }
    };
    loadAddressMarkerIcon();
  }, []);

  // Expose imperative methods via ref
  useImperativeHandle(ref, () => ({
    panTo: (location: GeoLocation) => {
      if (mapRef.current) {
        mapRef.current.panTo(location);
      }
    },
    setZoom: (newZoom: number) => {
      if (mapRef.current) {
        mapRef.current.setZoom(newZoom);
      }
    },
    getMap: () => mapRef.current,
  }));

  const hasValidMapCenter = mapCenter && (mapCenter.lat !== 0 || mapCenter.lng !== 0);

  // Handle center changes after map is loaded
  useEffect(() => {
    if (mapRef.current && mapCenter &&
        (previousCenterRef.current?.lat !== mapCenter.lat ||
         previousCenterRef.current?.lng !== mapCenter.lng)) {
      mapRef.current.panTo(mapCenter);
      previousCenterRef.current = mapCenter;
    }
  }, [mapCenter]);

  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    previousCenterRef.current = mapCenter;
    onMapLoad?.(map);
  };

  const handleMapTypeChange = (newMapTypeId: MapTypeId) => {
    // If not controlled, update internal state
    if (controlledMapTypeId === undefined) {
      setInternalMapTypeId(newMapTypeId);
    }
    onMapTypeChange?.(newMapTypeId);
  };


  const handleZoomIn = () => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom();
      if (currentZoom !== undefined) {
        mapRef.current.setZoom(currentZoom + 1);
      }
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom();
      if (currentZoom !== undefined) {
        mapRef.current.setZoom(currentZoom - 1);
      }
    }
  };

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
      libraries={LIBRARIES}
    >
      <div className="relative w-full h-full">
        {!hasValidMapCenter && (
          <div className="no-location-overlay absolute inset-0 z-10 flex items-center justify-center bg-gray-100/80">
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <MapPinOff className="h-8 w-8" />
              <span className="text-sm text-center">{noLocationMessage}</span>
            </div>
          </div>
        )}
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          mapContainerClassName="terra-draw-map"
          center={mapCenter}
          zoom={zoom}
          onLoad={onLoad}
          onZoomChanged={() => {
            const currentZoom = mapRef.current?.getZoom();
            if (currentZoom !== undefined) {
              // If not controlled, update internal state
              if (controlledZoom === undefined) {
                setInternalZoom(currentZoom);
              }
              // Always call the callback if provided
              onZoomChange?.(currentZoom);
            }
          }}
          options={{
            mapTypeId,
            styles: isSuppressLandmarks
              ? []
              : [
                  {
                    featureType: "poi",
                    stylers: [{ visibility: "off" }],
                  },
                  {
                    featureType: "transit",
                    elementType: "labels.icon",
                    stylers: [{ visibility: "off" }],
                  },
                ],
            gestureHandling: "greedy",
            disableDefaultUI: true,
            clickableIcons: false, // Required for TerraDraw
            streetViewControl: true,
            streetViewControlOptions: {
              position: window.google?.maps?.ControlPosition?.TOP_CENTER || 6
            },
          }}
        >
          {children}
          {/* Address Search Marker */}
          {selectedAddressLocation && addressMarkerIconUrl && !suppressAddressMarker && (
            <Marker
              position={selectedAddressLocation}
              iconUrl={addressMarkerIconUrl}
              iconSize={20}
            />
          )}
        </GoogleMap>
        
        {!isSuppressToolbar && (
          <>
            <div className="absolute top-2.5 left-2.5 z-10">
              <SearchToolbar
                onAddressSelected={({ address, location, formattedAddress, smartyStreetAddress }) => {
                  setSelectedAddressLocation(location);
                  onAddressSelected?.({ address, location, formattedAddress, smartyStreetAddress });
                }}
                onLocationRequest={(location) => {
                  setSelectedAddressLocation(location);
                  if (mapRef.current) {
                    mapRef.current.panTo(location);
                    mapRef.current.setZoom(15);
                  }
                }}
                drawingMode={drawingMode}
                selectedShapeType={selectedShapeType}
                onDrawingModeChange={onDrawingModeChange}
                triggerPulse={triggerSearchToolbarPulse}
                enableSelectionToolbar={enableSelectionToolbar}
              />
            </div>
            <div className="absolute bottom-2.5 left-2.5 z-10">
              <BasicToolbar
                mapTypeId={mapTypeId}
                onMapTypeChange={handleMapTypeChange}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onLocationRequest={(location) => {
                  setSelectedAddressLocation(location);
                  if (mapRef.current) {
                    mapRef.current.panTo(location);
                    mapRef.current.setZoom(15);
                  }
                }}
              />
            </div>
          </>
        )}
      </div>
    </LoadScript>
  );
});

Map.displayName = 'Map';

export default Map;
